import { TKV2_ORDER_ATTACHMENT_API } from '@aloft/toolkit-shared';

import { useEffect } from 'react';

import { useGetTkv2AlertsAPI, usePutTkv2AlertsAPI } from '../../hooks/api/toolkitComponents.ts';
import { useEdgeTrigger } from '../../hooks/useEdgeTrigger.ts';
import { alertsMutationComplete, setAlerts } from '../../redux/features/alertsSlice.ts';
import { selectAlerts } from '../../redux/features/selectors/commentarySelectors/selectAlerts.ts';
import { useAppDispatch, useAppSelector } from '../../redux/hooks.ts';
import { DEFAULT_SYNC_DRIVER_DELAY_MS } from './consts.ts';

export const useAlertsSyncDriver = (orderID: string) => {
  const dispatch = useAppDispatch();
  const { mutate: alertsMutate } = usePutTkv2AlertsAPI();
  const { data: persistedAlertsData } = useGetTkv2AlertsAPI({
    pathParams: { orderID },
  });

  const alerts = useAppSelector(selectAlerts);
  const { editRevision = 0, serverRevision = 0 } = alerts?.syncState ?? {};

  useEffect(() => {
    if (!persistedAlertsData || alerts.syncState) return;

    const parsedData = TKV2_ORDER_ATTACHMENT_API.ALERTS_API.GET.RESPONSE.parse(persistedAlertsData);
    dispatch(setAlerts(parsedData.body));
  }, [persistedAlertsData]);

  const onSuccess = () => {
    dispatch(
      alertsMutationComplete({
        status: 'success',
        serverRevision: editRevision,
      }),
    );
  };

  const onError = () => {
    dispatch(
      alertsMutationComplete({
        status: 'error',
      }),
    );
  };

  const mutate = () =>
    alertsMutate(
      {
        pathParams: { orderID },
        body: TKV2_ORDER_ATTACHMENT_API.ALERTS_API.PUT.REQUEST.BODY.parse(alerts),
      },
      { onSuccess, onError },
    );

  const mutationTrigger = useEdgeTrigger({
    condition: editRevision > serverRevision,
    delayMs: DEFAULT_SYNC_DRIVER_DELAY_MS,
    action: mutate,
  });

  useEffect(() => {
    mutationTrigger.restartTimer();
  }, [editRevision, mutationTrigger]);
};
